import { useEffect, useId } from "react";
import { FiX } from "react-icons/fi";

type RoleAccess = {
  role: string;
  summary: string;
  access: string[];
};

type RoleGroup = {
  portal: string;
  signIn: string;
  roles: RoleAccess[];
};

const ROLE_GROUPS: RoleGroup[] = [
  {
    portal: "Production",
    signIn: "These roles sign in on a Production account.",
    roles: [
      {
        role: "Designer",
        summary: "Works the cases assigned to them.",
        access: [
          "Cases menu: Admin Cases, Redesigns, My Cases, Hold Cases, and Ready to Upload.",
          "Marks a case Designed only when they are the allocated designer.",
          "Adds allocation notes and replies in case chat.",
          "Employee dashboard shows their own overview, cases, and attendance.",
          "Can submit portal feedback.",
        ],
      },
      {
        role: "Senior Designer",
        summary: "Same case list as Designer, with QC on the cases they are named on.",
        access: [
          "Opens the same cases menu as Designer.",
          "Sets design units and passes QC2.",
          "Marks Designed on any case, not only their own.",
          "Passes QC1 only when they are the allocated captain.",
          "Adds allocation notes and uses case chat.",
          "Employee dashboard and feedback stay limited to their own work.",
        ],
      },
      {
        role: "Captain",
        summary: "Allocates work and runs hold, QC, and surgery viewing.",
        access: [
          "Allocates cases and receives new-case alerts.",
          "Hold, unhold, transfer, close, and edit an allocation.",
          "Sets design units, passes QC2, and marks Designed on any case.",
          "Passes QC1 when they are the allocated captain.",
          "Opens Surgery Slots to view bookings. Booking changes stay with CRM and Admin.",
          "Adds allocation notes, uses case chat, and sees their own employee dashboard.",
        ],
      },
      {
        role: "Assistant Captain",
        summary: "Same allocation work as Captain, without Surgery Slots.",
        access: [
          "Allocates cases and receives new-case alerts.",
          "Hold, unhold, transfer, close, and edit an allocation.",
          "Sets design units, passes QC2, and marks Designed on any case.",
          "Passes QC1 when they are the allocated captain.",
          "Adds allocation notes, uses case chat, and sees their own employee dashboard.",
        ],
      },
    ],
  },
  {
    portal: "Operations",
    signIn: "These roles sign in on an Operations account.",
    roles: [
      {
        role: "CRM",
        summary: "Moves finished cases forward and edits surgery bookings.",
        access: [
          "Cases menu: Upload Case, Admin Cases, Redesigns, My Cases, Hold Cases, Ready to Upload, Surgery Slots, Lab Details, Case OK, and Design Ready.",
          "Completes a case, marks it corrupt, or sends it for internal redesign.",
          "Makes a case zip and opens the Case OK queue.",
          "Creates and edits surgery slots and bookings.",
          "Uploads files once the case is ready to upload or completed.",
          "Own employee dashboard, feedback submission, and case chat.",
          "Does not allocate, run QC, hold a case, or write allocation notes.",
          "Does not see Units, Lab Pricing, the team attendance report, or HR admin pages.",
        ],
      },
      {
        role: "Admin",
        summary: "Full case operations plus HR for the team. Can sign in on Operations or Production.",
        access: [
          "Signs in on Operations or Production accounts (dual portal).",
          "Full cases menu, including Mistakes, Units, Units Ledger, Marked Units, and Export Data.",
          "Allocates cases and runs every allocation step, including QC1 on any case.",
          "Makes zips, edits surgery bookings, and uploads once a case is ready.",
          "Sees every employee’s dashboard, cases, and the team attendance report.",
          "HR pages: People directory, Leaves, Organization, and Shifts.",
          "Approves leave for Executive and staff.",
          "Sets leave balances for Executive and other employees — not their own.",
          "Manages the feedback inbox and uses the mistakes tracker.",
          "Uses case chat. Does not open Lab Pricing.",
        ],
      },
      {
        role: "Executive",
        summary: "Admin access, plus Lab Pricing.",
        access: [
          "Same case, allocation, HR, attendance, mistakes, and team-dashboard access as Admin.",
          "Opens and edits Lab Pricing.",
          "Approves leave for staff and other executives.",
          "Sets leave balances for other employees — not their own, and not Admin or Super Admin.",
          "Views Surgery Slots. Booking changes stay with CRM and Admin.",
          "Manages the feedback inbox and uses case chat.",
        ],
      },
      {
        role: "Operations",
        summary: "Sees the admin case menu. Employee dashboard is self-only, like CRM.",
        access: [
          "Same cases menu as Admin, including units, mistakes, exports, and portal feedback.",
          "Employee dashboard shows their own overview, cases, and attendance only.",
          "Can apply for their own leave only — no team leave approval or balance edits.",
          "Manages the feedback inbox and views Surgery Slots.",
          "Allocation screens and case chat are view-only.",
          "Does not allocate, write allocation notes, or open Directory, Organization, or Shifts.",
          "Does not open the team attendance report or Lab Pricing.",
        ],
      },
      {
        role: "Developer",
        summary: "CRM-style case menu. Employee dashboard is self-only, like CRM.",
        access: [
          "Cases menu matches CRM: upload, cases, redesigns, hold, ready to upload, surgery slots, lab details, case OK, and design ready.",
          "Views Surgery Slots.",
          "Employee dashboard shows their own overview, cases, and attendance only.",
          "Can apply for their own leave only — no team leave approval or balance edits.",
          "Edits the attendance notebook connection.",
          "Submits portal feedback and uses case chat.",
          "Does not allocate, write allocation notes, or manage the feedback inbox.",
          "Does not open Directory, Organization, Shifts, the team attendance report, or Lab Pricing.",
        ],
      },
    ],
  },
];

type Props = {
  open: boolean;
  onClose: () => void;
};

const RolesOverviewModal = ({ open, onClose }: Props) => {
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-[#E8E8E4] px-5 py-4">
          <div>
            <h2 id={titleId} className="text-lg font-semibold text-[#4A4A48]">
              Roles overview
            </h2>
            <p className="mt-1 text-sm text-[#8A8A85]">
              What each employee role can open and change. Super Admin is not listed.
            </p>
          </div>
          <button
            type="button"
            aria-label="Close roles overview"
            onClick={onClose}
            className="rounded-lg p-1 text-[#8A8A85] hover:bg-[#F0F0EC] hover:text-[#4A4A48]"
          >
            <FiX size={18} />
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-4">
          <div className="flex flex-col gap-6">
            {ROLE_GROUPS.map((group) => (
              <section key={group.portal}>
                <div className="mb-3">
                  <p className="text-xs font-semibold uppercase tracking-wider text-[#345952]">
                    {group.portal}
                  </p>
                  <p className="mt-0.5 text-sm text-[#8A8A85]">{group.signIn}</p>
                </div>
                <div className="flex flex-col gap-3">
                  {group.roles.map((item) => (
                    <article
                      key={item.role}
                      className="rounded-2xl border border-[#E8E8E4] bg-[#FDFBF2] p-4"
                    >
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <h3 className="text-base font-semibold text-[#4A4A48]">
                          {item.role}
                        </h3>
                        <span className="rounded-full bg-[#E8F0EE] px-2.5 py-0.5 text-xs font-medium text-[#345952]">
                          {group.portal}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-[#6B6B66]">{item.summary}</p>
                      <ul className="mt-3 list-disc space-y-1.5 pl-5 text-sm text-[#4A4A48]">
                        {item.access.map((line) => (
                          <li key={line}>{line}</li>
                        ))}
                      </ul>
                    </article>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RolesOverviewModal;
